import React, { useCallback, useMemo } from 'react'
import { Document } from '@contentful/rich-text-types'
import { VideoBlock } from '@/atoms/VideoBlock'
import {
  IFaqSectionFields,
  IMediaGalleryFields,
  IMediaGalleryItemFields,
  ISectionOverviewFields,
  IVariationContainerFields,
  IVideoBlockFields,
} from '@/types/generated-contentful'
import { Content, mapContent, MappedContent } from '@/utils/TorchGuildUtil'
import { Bios } from './Bios'
import ContentfulExperiment from './ContentfulExperiment'
import FAQ from './FAQ'
import MediaGallery from './MediaGallery'
import { Testimonials } from './Testimonials'
import TextBlock from './TextBlock'

const CONTENT_BLOCK_LAYOUT = {
  BIO: 'bio',
  CARD: 'card',
  TESTIMONIAL: 'testimonial',
}

interface Props {
  section: ISectionOverviewFields
}

export const Overview = ({ section }: Props) => {
  const mapToComponent = useCallback(
    (itemRaw: ISectionOverviewFields['content'][number] | IVariationContainerFields['variations'][number]) => {
      const item = mapContent(itemRaw as unknown as Content) as MappedContent & { items: Content[]; text: Document }
      if (!item) return null

      return (
        <div key={item.id} className="mb-16 last:mb-0">
          {item.contentType === 'sectionContentBlock' && item.layout === CONTENT_BLOCK_LAYOUT.BIO && (
            <Bios items={item.items} />
          )}
          {item.contentType === 'sectionContentBlock' && item.layout === CONTENT_BLOCK_LAYOUT.TESTIMONIAL && (
            <Testimonials items={item.items} />
          )}
          {item.contentType === 'faqSection' && <FAQ {...(item as unknown as IFaqSectionFields)} />}
          {item.contentType === 'textBlock' && <TextBlock document={item.text} />}
          {item.contentType === 'mediaGallery' && (
            <MediaGallery
              {...(item as unknown as IMediaGalleryFields)}
              items={item.items.map(mapContent) as unknown as (IMediaGalleryItemFields & { id: string })[]}
            />
          )}
          {item.contentType === 'variationContainer' && (
            <ContentfulExperiment
              experimentTitle={String(item.experimentTitle)}
              variations={item.variations as IVariationContainerFields['variations']}
            >
              {(variation) => mapToComponent(variation)}
            </ContentfulExperiment>
          )}
          {item.contentType === 'videoBlock' && <VideoBlock {...(item as unknown as IVideoBlockFields)} />}
        </div>
      )
    },
    [],
  )
  const items = useMemo(() => section.content.map(mapToComponent), [mapToComponent, section.content])

  return <div className="h-full">{items}</div>
}

export default Overview
